import React from "react";
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  MessageSquare,
  Phone,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { type BaseComponentProps } from "../../types/puck";
import { useTurnstile } from "../../hooks/use-turnstile";
import {
  getWindowJobsiteConfig,
  type JobsiteBusinessContext,
} from "../../types/runtime-config";
import { Render, type Data } from "@puckeditor/core";
import {
  EMPTY_FORM_DATA,
  fieldsFromData,
  missingRequiredFromData,
  noteConfigFromData,
  stepTextFromData,
  type FieldValue,
} from "../../forms/form-data";
import { FormValuesProvider } from "../../forms/form-values-context";
import { submitLead } from "../../forms/submit-lead";
import { fieldValueText, parkedBookingPayload, type ParkedBookingInput } from "./booking-fallback";
import { FormEmbedSkeleton } from "../../forms/form-embed";
import { formsPuckConfig } from "../../forms/form-fields.config";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  createBooking,
  fetchBookingPublic,
  sendOtp,
  uploadFormImage,
  verifyOtp,
  type BookingPro,
  type BookingService,
  type VerifiedCustomer,
} from "./booking-api";

/* ────────────────────────── helpers ────────────────────────── */

/** "5125550148" typed live → "(512) 555-0148". */
function fmtPhone(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return "";
}

/** Display number → "+15125550148" (the API normalizes again; we send clean E.164). */
function toE164(display: string): string {
  return `+1${display.replace(/\D/g, "").slice(0, 10)}`;
}

/** Analytics session, unless this is an editor preview (which must not attribute). */
function currentSessionId(): string | null {
  if (typeof window === "undefined") return null;
  const isPreview = new URLSearchParams(window.location.search).get("preview") === "true";
  return isPreview ? null : sessionStorage.getItem("sp_session_id") || null;
}

interface RequestDay {
  iso: string; // YYYY-MM-DD
  dow: string; // "Wed"
  day: number; // 18
  label: string; // "Today" | "Tomorrow" | "Wed, Jun 18"
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** 14 selectable days starting today. Request-mode: every day is open (no
 *  availability lookup) — the pro confirms the exact time afterward. */
function buildRequestDays(): RequestDay[] {
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  return Array.from({ length: 14 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const yyyy = d.getFullYear();
    const mm = `${d.getMonth() + 1}`.padStart(2, "0");
    const dd = `${d.getDate()}`.padStart(2, "0");
    return {
      iso: `${yyyy}-${mm}-${dd}`,
      dow: DOW[d.getDay()],
      day: d.getDate(),
      label:
        i === 0
          ? "Today"
          : i === 1
            ? "Tomorrow"
            : `${DOW[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()}`,
    };
  });
}

type WindowId = "asap" | "morning" | "afternoon" | "evening";
const WINDOWS: { id: WindowId; label: string; hint: string }[] = [
  { id: "asap", label: "Soonest available", hint: "I'm flexible" },
  { id: "morning", label: "Morning", hint: "8am – 12pm" },
  { id: "afternoon", label: "Afternoon", hint: "12pm – 5pm" },
  { id: "evening", label: "Evening", hint: "5pm – 8pm" },
];

function money(n: number): string {
  return `$${n}`;
}
/** Price chip — empty when the pro hasn't set a price (we don't want "Estimate"
 *  plastered across every service; absence reads cleaner than a filler word). */
function priceLabel(s: BookingService): string {
  return s.price != null ? `${s.priceQualifier ? `${s.priceQualifier} ` : ""}${money(s.price)}` : "";
}

const UPPER_LABEL =
  "jobsite:text-[11px] jobsite:font-bold jobsite:uppercase jobsite:tracking-[0.08em] jobsite:text-muted-foreground";

/* ──────────────────── sub-components ──────────────────── */

function DayRail({
  days,
  selectedIso,
  onSelect,
}: {
  days: RequestDay[];
  selectedIso: string;
  onSelect: (iso: string) => void;
}) {
  return (
    <div>
      <div className={UPPER_LABEL}>Pick a day</div>
      <div className="jobsite:mt-2.5 jobsite:flex jobsite:gap-1.5 jobsite:overflow-x-auto jobsite:pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {days.map((d) => {
          const sel = d.iso === selectedIso;
          return (
            <button
              key={d.iso}
              type="button"
              onClick={() => onSelect(d.iso)}
              className="jobsite:flex jobsite:w-[44px] jobsite:shrink-0 jobsite:flex-col jobsite:items-center jobsite:gap-1.5"
            >
              <span className="jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:text-muted-foreground">
                {d.label === "Today" ? "Today" : d.dow}
              </span>
              <span
                className={cn(
                  "jobsite:flex jobsite:h-[42px] jobsite:w-[42px] jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:border jobsite:font-heading jobsite:text-base jobsite:transition-colors",
                  sel
                    ? "jobsite:border-primary jobsite:bg-primary jobsite:text-primary-foreground"
                    : "jobsite:border-border jobsite:text-foreground jobsite:hover:border-primary",
                )}
              >
                {d.day}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WindowGrid({
  value,
  onSelect,
}: {
  value: WindowId | null;
  onSelect: (w: WindowId) => void;
}) {
  return (
    <div>
      <div className={UPPER_LABEL}>Preferred time</div>
      <div className="jobsite:mt-2.5 jobsite:grid jobsite:grid-cols-1 jobsite:gap-2.5 jobsite:sm:grid-cols-2">
        {WINDOWS.map((w) => {
          const sel = value === w.id;
          return (
            <button
              key={w.id}
              type="button"
              onClick={() => onSelect(w.id)}
              className={cn(
                "jobsite:rounded-xl jobsite:border jobsite:px-3.5 jobsite:py-3 jobsite:text-left jobsite:transition-colors",
                sel
                  ? "jobsite:border-primary jobsite:bg-primary/10"
                  : "jobsite:border-border jobsite:bg-background jobsite:hover:border-primary/50",
              )}
            >
              <span
                className={cn(
                  "jobsite:block jobsite:text-[15px] jobsite:font-semibold",
                  sel ? "jobsite:text-primary" : "jobsite:text-foreground",
                )}
              >
                {w.label}
              </span>
              <span className="jobsite:mt-0.5 jobsite:block jobsite:text-xs jobsite:text-muted-foreground">
                {w.hint}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OtpBoxes({ code, setCode }: { code: string; setCode: (s: string) => void }) {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    const t = window.setTimeout(() => ref.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, []);
  const digits = code.split("");
  return (
    <div
      className="jobsite:relative jobsite:flex jobsite:justify-center jobsite:gap-2"
      onClick={() => ref.current?.focus()}
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={cn(
            "jobsite:flex jobsite:h-[56px] jobsite:max-w-[48px] jobsite:flex-1 jobsite:items-center jobsite:justify-center jobsite:rounded-xl jobsite:border-2 jobsite:bg-background jobsite:font-heading jobsite:text-2xl jobsite:text-foreground",
            digits[i] || i === code.length
              ? "jobsite:border-primary"
              : "jobsite:border-border",
          )}
        >
          {digits[i] ?? ""}
        </div>
      ))}
      <input
        ref={ref}
        type="tel"
        inputMode="numeric"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="jobsite:pointer-events-none jobsite:absolute jobsite:h-px jobsite:w-px jobsite:opacity-0"
      />
    </div>
  );
}

function SummaryRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="jobsite:flex jobsite:items-start jobsite:gap-3 jobsite:border-b jobsite:border-border/60 jobsite:py-3 jobsite:last:border-b-0">
      <span className="jobsite:flex jobsite:h-9 jobsite:w-9 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:rounded-lg jobsite:bg-primary/10 jobsite:text-primary">
        {icon}
      </span>
      <div className="jobsite:min-w-0">
        <div className="jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.04em] jobsite:text-muted-foreground">
          {label}
        </div>
        <div className="jobsite:mt-0.5 jobsite:text-[14.5px] jobsite:font-semibold jobsite:leading-snug jobsite:text-foreground">
          {children}
        </div>
      </div>
    </div>
  );
}

function CallButton({ phone, label }: { phone: string; label: string }) {
  return (
    <Button asChild variant="outline" size="lg" className="jobsite:h-12 jobsite:w-full">
      <a href={`tel:${phone.replace(/\D/g, "")}`}>
        <Phone className="jobsite:h-4 jobsite:w-4" /> {label}
      </a>
    </Button>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="jobsite:mx-auto jobsite:block jobsite:text-sm jobsite:font-medium jobsite:text-muted-foreground jobsite:underline-offset-4 jobsite:hover:text-foreground jobsite:hover:underline"
    >
      Back
    </button>
  );
}

/* ──────────────────── the inline flow card ──────────────────── */

export interface BookingWidgetInlineProps {
  slug?: string;
  apiUrl?: string;
  businessName?: string;
  businessPhone?: string;
  /** Heading shown above the service picker. */
  title?: string;
  /** Optional supporting line under the title (most steps supply their own). */
  subtitle?: string;
  className?: string;
  /** Dashboard preview: render the real flow from a draft form (no network) so
   *  the form builder shows the whole widget — picker first, then custom fields. */
  preview?: { formData: Data; services?: BookingService[] };
  /** Form-builder mode: the host drives navigation, gating is relaxed, and the
   *  step buttons advance without hitting the network (so the whole flow is
   *  clickable as a preview). The Details step renders `renderFields()`. */
  editor?: boolean;
  /** Slot for the Details-step field area. The builder passes an editable Puck
   *  canvas here; live surfaces leave it unset and get the saved <Render>. */
  renderFields?: () => React.ReactNode;
  /** Controlled current step (editor). When set, the host owns navigation. */
  step?: Step;
  onStepChange?: (step: Step) => void;
}

/** One sample request-mode service so the preview's picker has something to show. */
const PREVIEW_SERVICES: BookingService[] = [
  {
    id: "preview",
    name: "Service request",
    slug: "service-request",
    blurb: "",
    duration: null,
    price: null,
    priceQualifier: null,
    deposit: null,
    popular: false,
    urgent: false,
    schedulingMode: "request",
  },
];

type Step = "select" | "details" | "phone" | "otp" | "profile" | "review" | "done";

/** Public alias + ordered metadata so the form builder can render a step
 *  navigator that mirrors the live wizard exactly. */
export type BookingStep = Step;
export const BOOKING_STEPS: { id: Step; label: string }[] = [
  { id: "select", label: "Service & time" },
  { id: "details", label: "Details" },
  { id: "phone", label: "Verify" },
  { id: "otp", label: "Code" },
  { id: "profile", label: "Your info" },
  { id: "review", label: "Review" },
  { id: "done", label: "Done" },
];

/** Default wording per step. The form builder edits these (stored as overrides
 *  under root.props.stepText); the widget falls back here when an override is
 *  blank. Steps with dynamic subtitles (otp, done) omit one. */
export const BOOKING_STEP_DEFAULTS: Record<Step, { title: string; subtitle?: string }> = {
  select: { title: "Book a service", subtitle: "" },
  details: { title: "A few details", subtitle: "Tell us a bit more so we come prepared." },
  phone: { title: "Verify it's you", subtitle: "We'll text a 6-digit code to confirm your request." },
  otp: { title: "Enter the code" },
  profile: { title: "Your details", subtitle: "Just a name and email so we can reach you." },
  review: { title: "Review request", subtitle: "They'll confirm the exact time." },
  done: { title: "Request sent." },
};

export const BookingWidgetInline: React.FC<BookingWidgetInlineProps> = ({
  slug,
  apiUrl,
  businessName,
  businessPhone,
  title = "Book a service",
  subtitle = "",
  className,
  preview,
  editor,
  renderFields,
  step: controlledStep,
  onStepChange,
}) => {
  const [load, setLoad] = React.useState<"loading" | "ready" | "unavailable" | "offline">(
    "loading",
  );
  const [pro, setPro] = React.useState<BookingPro | null>(null);
  const [services, setServices] = React.useState<BookingService[]>([]);
  const [formData, setFormData] = React.useState<Data>(EMPTY_FORM_DATA);
  const days = React.useMemo(buildRequestDays, []);
  // Where a request goes when the API can't take it (SP-400); absent → no fallback.
  const { backupApiUrl, siteId } = getWindowJobsiteConfig();

  // selection
  const [svc, setSvc] = React.useState<BookingService | null>(null);
  const [dayIso, setDayIso] = React.useState(days[0].iso);
  const [win, setWin] = React.useState<WindowId | null>(null);
  const [note, setNote] = React.useState("");
  // Custom (form-builder) field values, keyed by field.key → formData on submit.
  const [customValues, setCustomValues] = React.useState<Record<string, FieldValue>>({});

  // identity
  // Editor mode lets the form builder drive navigation; live mode self-manages.
  const [stepState, setStepState] = React.useState<Step>("select");
  const step = controlledStep ?? stepState;
  const setStep = (s: Step) => {
    onStepChange?.(s);
    if (controlledStep === undefined) setStepState(s);
  };
  const [phone, setPhone] = React.useState("");
  const [smsOptIn, setSmsOptIn] = React.useState(false);
  const [code, setCode] = React.useState("");
  const [verification, setVerification] = React.useState<{
    id: string;
    customer: VerifiedCustomer;
  } | null>(null);
  const [profile, setProfile] = React.useState({ first: "", last: "", email: "" });
  const [termsConsent, setTermsConsent] = React.useState(false);

  // async ui
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string>();
  const [confCode, setConfCode] = React.useState("");
  // SP-400: this became a lead for a human to schedule, not a confirmed booking.
  const [parked, setParked] = React.useState(false);
  const [offlineForm, setOfflineForm] = React.useState({
    name: "",
    phone: "",
    email: "",
    need: "",
    when: "",
  });

  // Bot protection — reuses the jobsite's per-site Turnstile (same key the
  // contact form uses). No-op when the site has no Turnstile configured.
  const {
    tokenRef: turnstileTokenRef,
    containerRef: turnstileContainerRef,
    containerClassName: turnstileClassName,
  } = useTurnstile();

  React.useEffect(() => {
    let alive = true;
    // Preview (form builder): seed from the draft, skip the network entirely.
    if (preview) {
      const svcs = preview.services ?? PREVIEW_SERVICES;
      setPro({ name: businessName ?? "Your business", slug: "", trade: null, area: null, phone: null });
      setServices(svcs);
      if (svcs.length === 1) setSvc(svcs[0]);
      setLoad("ready");
      return;
    }
    if (!slug || !apiUrl) {
      setLoad("unavailable");
      return;
    }
    fetchBookingPublic(apiUrl, slug).then((res) => {
      if (!alive) return;
      if (!res.ok) {
        // A refusal is this business's configuration; only an outage may capture.
        setLoad(res.offline && backupApiUrl && siteId ? "offline" : "unavailable");
        return;
      }
      // Only request-mode services work here: the server derives instant vs
      // request from the service config, and instant needs real availability
      // (not built). Instant services route to the full booking page instead.
      const requestable = res.data.services.filter((s) => s.schedulingMode === "request");
      if (requestable.length === 0) {
        setLoad("unavailable");
        return;
      }
      setPro(res.data.pro);
      setServices(requestable);
      setFormData(res.data.formData);
      if (requestable.length === 1) setSvc(requestable[0]);
      setLoad("ready");
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, apiUrl]);

  // Keep the preview form in sync with the live draft, without resetting the
  // flow (selection/step) the way a full reseed would.
  React.useEffect(() => {
    if (preview) setFormData(preview.formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.formData]);

  const known = verification?.customer.exists ?? false;
  const name = known
    ? `${verification!.customer.firstName ?? ""} ${verification!.customer.lastName ?? ""}`.trim()
    : `${profile.first} ${profile.last}`.trim();
  const windowLabel = WINDOWS.find((w) => w.id === win)?.label ?? "";
  const dayLabel = days.find((d) => d.iso === dayIso)?.label ?? "";

  const phoneNumber = businessPhone ?? pro?.phone ?? null;

  /** Hold a request for a human, retrying the primary in case it has recovered. */
  const park = async (input: ParkedBookingInput): Promise<boolean> => {
    if (!apiUrl || !siteId || !backupApiUrl) return false;
    // Unspent: sendOtp is the only step that consumes a token, and parking it
    // means that call never reached a server that could have validated one.
    const turnstileToken = turnstileTokenRef.current;
    const result = await submitLead({
      apiUrl,
      siteId,
      backupApiUrl,
      businessName: businessName ?? pro?.name ?? undefined,
      payload: {
        ...parkedBookingPayload({ ...input, sessionId: currentSessionId() }),
        ...(turnstileToken ? { turnstileToken } : {}),
      },
    });
    if (result.status === "failed") return false;
    // True even when the primary took it: nothing here scheduled a time.
    setParked(true);
    setStep("done");
    return true;
  };

  /** Everything the visitor has given the flow so far, as a lead. */
  const parkFromFlow = async (): Promise<boolean> => {
    const labels = new Map(fieldsFromData(formData).map((f) => [f.id, f.label]));
    return park({
      phone: toE164(phone),
      name: name || undefined,
      email: profile.email || undefined,
      service: svc?.name,
      preferredTime: [dayLabel, windowLabel].filter(Boolean).join(" · "),
      note,
      details: Object.entries(customValues).map(([id, value]) => ({
        label: labels.get(id) ?? id,
        value: fieldValueText(value),
      })),
    });
  };

  const send = async () => {
    if (editor) {
      setStep("otp");
      return;
    }
    // If the site enforces Turnstile, require a solved token before spending SMS.
    if (getWindowJobsiteConfig()?.turnstileSiteKey && !turnstileTokenRef.current) {
      setErr("Please complete the verification check and try again.");
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const res = await sendOtp(apiUrl!, toE164(phone), {
        slug,
        turnstileToken: turnstileTokenRef.current,
      });
      if (res.ok) {
        setCode("");
        setStep("otp");
        return;
      }
      if (res.offline && (await parkFromFlow())) return;
      setErr(res.error);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (editor) {
      setStep("review");
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const res = await verifyOtp(apiUrl!, toE164(phone), code);
      if (!res.ok) {
        if (res.offline && (await parkFromFlow())) return;
        setErr(res.error);
        return;
      }
      setVerification({ id: res.verificationId, customer: res.customer });
      if (res.customer.exists) {
        setProfile({
          first: res.customer.firstName ?? "",
          last: res.customer.lastName ?? "",
          email: res.customer.email ?? "",
        });
        setStep("review");
      } else {
        setStep("profile");
      }
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (editor) {
      setConfCode("ABC123");
      setStep("done");
      return;
    }
    if (!verification || !svc) return;
    setBusy(true);
    setErr(undefined);
    try {
      const res = await createBooking(apiUrl!, {
        verificationId: verification.id,
        slug: slug!,
        serviceId: svc.id,
        firstName: profile.first || undefined,
        lastName: profile.last || undefined,
        email: profile.email || undefined,
        consent: known ? true : termsConsent,
        smsOptIn,
        request: { preferredDate: dayIso, window: win ?? "asap", note: note.trim() || undefined },
        customFields: Object.keys(customValues).length ? customValues : undefined,
        sessionId: currentSessionId(),
      });
      if (res.ok) {
        setConfCode(res.booking.confCode);
        setStep("done");
        return;
      }
      if (res.offline && (await parkFromFlow())) return;
      setErr(res.error);
    } finally {
      setBusy(false);
    }
  };

  /* ── loading / unavailable fallbacks ── */
  if (load === "loading") {
    // Form-shaped silhouette (≈ the picker step's height) so the widget doesn't
    // jump when the real content arrives.
    return (
      <Card className={cn("jobsite:border jobsite:shadow-xl", className)}>
        <CardContent className="jobsite:p-6 jobsite:sm:p-8">
          <FormEmbedSkeleton fields={3} />
        </CardContent>
      </Card>
    );
  }
  if (load === "unavailable") {
    // No bookable services — fall back to a call CTA so the slot is never dead.
    return (
      <Card className={cn("jobsite:border jobsite:shadow-xl", className)}>
        <CardContent className="jobsite:space-y-3 jobsite:p-6 jobsite:text-center">
          <p className="jobsite:font-heading jobsite:text-xl jobsite:text-card-foreground">
            {title}
          </p>
          <p className="jobsite:text-sm jobsite:text-muted-foreground">
            {phoneNumber
              ? "Reach out and we'll get you scheduled."
              : "Get in touch and we'll get you scheduled."}
          </p>
          {phoneNumber && (
            <Button asChild size="lg" className="jobsite:h-12 jobsite:w-full jobsite:font-semibold">
              <a href={`tel:${phoneNumber.replace(/\D/g, "")}`}>
                <Phone className="jobsite:h-4 jobsite:w-4" /> Call to book
              </a>
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // No services and no times to offer, so ask in free text; a call CTA alone loses everyone who won't phone.
  if (load === "offline" && !parked) {
    const ready =
      offlineForm.name.trim() !== "" &&
      offlineForm.phone.replace(/\D/g, "").length === 10 &&
      offlineForm.need.trim() !== "";
    return (
      <Card className={cn("jobsite:border jobsite:shadow-xl", className)}>
        <CardContent className="jobsite:space-y-4 jobsite:p-6 jobsite:sm:p-8">
          <div className="jobsite:space-y-1">
            <p className="jobsite:font-heading jobsite:text-xl jobsite:text-card-foreground">
              {title}
            </p>
            <p className="jobsite:text-sm jobsite:text-muted-foreground">
              Online times aren't loading right now. Leave your details and{" "}
              {businessName ?? "we"}'ll call to schedule.
            </p>
          </div>
          <Input
            placeholder="Your name"
            autoComplete="name"
            value={offlineForm.name}
            onChange={(e) => setOfflineForm((f) => ({ ...f, name: e.target.value }))}
            className="jobsite:h-12 jobsite:text-base"
          />
          <Input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="(512) 555-0148"
            value={offlineForm.phone}
            onChange={(e) => setOfflineForm((f) => ({ ...f, phone: fmtPhone(e.target.value) }))}
            className="jobsite:h-12 jobsite:text-base"
          />
          <Input
            type="email"
            autoComplete="email"
            placeholder="Email (optional)"
            value={offlineForm.email}
            onChange={(e) => setOfflineForm((f) => ({ ...f, email: e.target.value }))}
            className="jobsite:h-12 jobsite:text-base"
          />
          <Textarea
            placeholder="What do you need done?"
            value={offlineForm.need}
            onChange={(e) => setOfflineForm((f) => ({ ...f, need: e.target.value }))}
            className="jobsite:min-h-24 jobsite:text-base"
          />
          <Input
            placeholder="When works for you? (e.g. weekday mornings)"
            value={offlineForm.when}
            onChange={(e) => setOfflineForm((f) => ({ ...f, when: e.target.value }))}
            className="jobsite:h-12 jobsite:text-base"
          />
          <div ref={turnstileContainerRef} className={turnstileClassName} />
          {err && <p className="jobsite:text-sm jobsite:font-medium jobsite:text-destructive">{err}</p>}
          <Button
            type="button"
            size="lg"
            disabled={!ready || busy}
            onClick={async () => {
              setBusy(true);
              setErr(undefined);
              const sent = await park({
                phone: toE164(offlineForm.phone),
                name: offlineForm.name,
                email: offlineForm.email,
                preferredTime: offlineForm.when,
                note: offlineForm.need,
              });
              setBusy(false);
              if (!sent) {
                setErr(
                  phoneNumber
                    ? `That didn't send. Please call ${phoneNumber}.`
                    : "That didn't send. Please try again shortly.",
                );
              }
            }}
            className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
          >
            {busy ? <Loader2 className="jobsite:h-4 jobsite:w-4 jobsite:animate-spin" /> : "Send request"}
          </Button>
          {phoneNumber && <CallButton phone={phoneNumber} label="Call instead" />}
        </CardContent>
      </Card>
    );
  }

  /* ── success ── */
  if (step === "done") {
    return (
      <Card className={cn("jobsite:border jobsite:shadow-xl", className)}>
        <CardContent className="jobsite:space-y-4 jobsite:p-6 jobsite:text-center">
          <div className="jobsite:mx-auto jobsite:flex jobsite:h-16 jobsite:w-16 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-primary jobsite:text-primary-foreground">
            <MessageSquare className="jobsite:h-7 jobsite:w-7" />
          </div>
          <div className="jobsite:space-y-1">
            <p className="jobsite:font-heading jobsite:text-2xl jobsite:text-card-foreground">
              {parked
                ? "Request received."
                : stepTextFromData(formData).done?.title?.trim() || "Request sent."}
            </p>
            <p className="jobsite:text-sm jobsite:text-muted-foreground">
              {parked
                ? `${businessName ?? pro?.name ?? "We"} couldn't confirm a time just now, so they'll call you to schedule.`
                : `${businessName ?? pro?.name ?? "We"} will text ${profile.first || "you"} to confirm a time.`}
            </p>
          </div>
          {parked ? (
            phoneNumber && <CallButton phone={phoneNumber} label={`Call ${businessName ?? "us"}`} />
          ) : (
            <div className="jobsite:rounded-xl jobsite:border jobsite:border-border jobsite:bg-muted/40 jobsite:px-4 jobsite:py-3 jobsite:text-sm">
              <span className="jobsite:text-muted-foreground">Confirmation</span>{" "}
              <span className="jobsite:font-mono jobsite:font-semibold jobsite:text-foreground">
                {confCode || (editor ? "ABC123" : "")}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Custom fields get their own step (a wizard) rather than crowding the picker.
  const hasCustomFields = fieldsFromData(formData).length > 0;
  const note_ = noteConfigFromData(formData);
  const noteMissing = note_.enabled && note_.required && !note.trim();
  const detailsComplete = missingRequiredFromData(formData, customValues).length === 0;
  const afterSelect: Step = hasCustomFields ? "details" : "phone"; // select's Continue target
  const beforePhone: Step = hasCustomFields ? "details" : "select"; // phone's Back target

  /* ── header (varies by step; per-step overrides win, else the default) ── */
  const stepText = stepTextFromData(formData);
  const ovTitle = (s: Step, fallback: string) => {
    const t = stepText[s]?.title;
    return t && t.trim() ? t : fallback;
  };
  const ovSub = (s: Step, fallback: string) => {
    const v = stepText[s]?.subtitle;
    return v === undefined ? fallback : v;
  };
  const headerByStep: Record<Exclude<Step, "done">, { title: string; sub: string }> = {
    select: { title: ovTitle("select", title), sub: ovSub("select", subtitle) },
    details: {
      title: ovTitle("details", BOOKING_STEP_DEFAULTS.details.title),
      sub: ovSub("details", BOOKING_STEP_DEFAULTS.details.subtitle!),
    },
    phone: {
      title: ovTitle("phone", BOOKING_STEP_DEFAULTS.phone.title),
      sub: ovSub("phone", BOOKING_STEP_DEFAULTS.phone.subtitle!),
    },
    otp: {
      title: ovTitle("otp", BOOKING_STEP_DEFAULTS.otp.title),
      sub: `Sent to ${phone || (editor ? "(512) 555-0148" : "")}.`,
    },
    profile: {
      title: ovTitle("profile", BOOKING_STEP_DEFAULTS.profile.title),
      sub: ovSub("profile", BOOKING_STEP_DEFAULTS.profile.subtitle!),
    },
    review: {
      title: ovTitle("review", BOOKING_STEP_DEFAULTS.review.title),
      sub: ovSub("review", BOOKING_STEP_DEFAULTS.review.subtitle!),
    },
  };
  const header = headerByStep[step];

  return (
    <Card className={cn("jobsite:overflow-hidden jobsite:border jobsite:shadow-xl", className)}>
      <CardContent className="jobsite:p-6 jobsite:sm:p-7">
        <div className="jobsite:mb-5 jobsite:space-y-1">
          <p className="jobsite:font-heading jobsite:text-xl jobsite:text-card-foreground">
            {header.title}
          </p>
          {header.sub && (
            <p className="jobsite:text-sm jobsite:text-muted-foreground">{header.sub}</p>
          )}
        </div>

        {/* ── SELECT: service + day + window + note ── */}
        {step === "select" && (
          <div className="jobsite:space-y-5">
            <div className="jobsite:space-y-2">
              <div className={UPPER_LABEL}>Service</div>
              {services.length === 1 ? (
                <div className="jobsite:rounded-xl jobsite:border jobsite:border-border jobsite:bg-muted/40 jobsite:px-4 jobsite:py-3">
                  <span className="jobsite:text-[15px] jobsite:font-semibold jobsite:text-foreground">
                    {services[0].name}
                  </span>
                  {priceLabel(services[0]) && (
                    <span className="jobsite:text-sm jobsite:text-muted-foreground">
                      {" · "}
                      {priceLabel(services[0])}
                    </span>
                  )}
                </div>
              ) : (
                <div className="jobsite:relative">
                  <select
                    value={svc?.id ?? ""}
                    onChange={(e) =>
                      setSvc(services.find((s) => s.id === e.target.value) ?? null)
                    }
                    className="jobsite:h-12 jobsite:w-full jobsite:appearance-none jobsite:rounded-md jobsite:border jobsite:border-input jobsite:bg-transparent jobsite:pl-3 jobsite:pr-10 jobsite:text-base jobsite:text-foreground jobsite:outline-none jobsite:focus-visible:border-ring jobsite:focus-visible:ring-ring/50 jobsite:focus-visible:ring-[3px]"
                  >
                    <option value="">Select a service</option>
                    {services.map((s) => {
                      const price = priceLabel(s);
                      return (
                        <option key={s.id} value={s.id}>
                          {price ? `${s.name} · ${price}` : s.name}
                        </option>
                      );
                    })}
                  </select>
                  <ChevronDown className="jobsite:pointer-events-none jobsite:absolute jobsite:right-3 jobsite:top-1/2 jobsite:h-4 jobsite:w-4 jobsite:-translate-y-1/2 jobsite:text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Day + time always show — request-mode has no availability gate, so
                the picker is the inviting default rather than an empty shell. */}
            <DayRail days={days} selectedIso={dayIso} onSelect={setDayIso} />
            <WindowGrid value={win} onSelect={setWin} />
            {note_.enabled && (
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder={note_.required ? note_.label : `${note_.label} (optional)`}
                className="jobsite:resize-none jobsite:text-sm"
              />
            )}
            <Button
              type="button"
              size="lg"
              disabled={!editor && (!svc || !win || noteMissing)}
              onClick={() => setStep(afterSelect)}
              className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
            >
              Continue <ChevronRight className="jobsite:h-4 jobsite:w-4" />
            </Button>
          </div>
        )}

        {/* ── DETAILS (form-builder fields) — the form's Puck document rendered
            by the same config the editor uses; FormValuesProvider collects. ── */}
        {step === "details" && (
          <div className="jobsite:space-y-4">
            {renderFields ? (
              renderFields()
            ) : (
              <FormValuesProvider
                values={customValues}
                onChange={(id, value) => setCustomValues((prev) => ({ ...prev, [id]: value }))}
                uploadImage={async (file) => {
                  const res = await uploadFormImage(apiUrl!, slug!, file);
                  return res.ok ? res.url : null;
                }}
              >
                <Render config={formsPuckConfig} data={formData} />
              </FormValuesProvider>
            )}
            <div className="jobsite:space-y-3">
              <Button
                type="button"
                size="lg"
                disabled={!editor && !detailsComplete}
                onClick={() => setStep("phone")}
                className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
              >
                Continue <ChevronRight className="jobsite:h-4 jobsite:w-4" />
              </Button>
              <BackLink onClick={() => setStep("select")} />
            </div>
          </div>
        )}

        {/* ── PHONE ── */}
        {step === "phone" && (
          <div className="jobsite:space-y-4">
            <Input
              type="tel"
              inputMode="numeric"
              autoFocus
              placeholder="(512) 555-0148"
              value={phone}
              onChange={(e) => setPhone(fmtPhone(e.target.value))}
              className="jobsite:h-12 jobsite:text-base"
            />
            {/* On the phone step, not after the code, so a carrier reviewer reaches it
                without a phone code. Separate from Send code and never required; the
                wording names who sends, what, how often, rates, STOP, HELP and the two
                links the verification submits (Twilio 30505, SP-664). */}
            <label className="jobsite:flex jobsite:cursor-pointer jobsite:items-start jobsite:gap-2.5">
              <input
                type="checkbox"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                className="jobsite:mt-0.5 jobsite:h-[18px] jobsite:w-[18px] jobsite:shrink-0 jobsite:accent-primary"
              />
              <span className="jobsite:text-[12px] jobsite:leading-relaxed">
                Text me updates about this booking{" "}
                <span className="jobsite:text-muted-foreground">(optional)</span>
                <span className="jobsite:block jobsite:text-[11px] jobsite:text-muted-foreground">
                  Booking confirmations, reminders and replies from{" "}
                  {businessName ?? pro?.name ?? "this business"}, sent through ServicePro.co. Message
                  frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for
                  help.{" "}
                  <a
                    href="https://servicepro.co/sms-terms"
                    target="_blank"
                    rel="noreferrer"
                    className="jobsite:underline"
                  >
                    SMS Terms
                  </a>{" "}
                  ·{" "}
                  <a
                    href="https://servicepro.co/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                    className="jobsite:underline"
                  >
                    Privacy
                  </a>
                </span>
              </span>
            </label>
            {/* Turnstile renders here when the site has a key (CF managed mode). */}
            <div ref={turnstileContainerRef} className={turnstileClassName} />
            {err && <p className="jobsite:text-sm jobsite:font-medium jobsite:text-destructive">{err}</p>}
            <div className="jobsite:space-y-3">
              <Button
                type="button"
                size="lg"
                disabled={!editor && (phone.replace(/\D/g, "").length !== 10 || busy)}
                onClick={send}
                className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
              >
                {busy ? <Loader2 className="jobsite:h-4 jobsite:w-4 jobsite:animate-spin" /> : "Send code"}
              </Button>
              <BackLink onClick={() => setStep(beforePhone)} />
            </div>
            {/* Only the code the person just asked for; booking texts are the box above. */}
            <p className="jobsite:text-center jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground">
              We&apos;ll text you a one-time code. Msg &amp; data rates may apply. See{" "}
              <a
                href="https://servicepro.co/sms-terms"
                target="_blank"
                rel="noreferrer"
                className="jobsite:underline"
              >
                SMS Terms
              </a>{" "}
              &amp;{" "}
              <a
                href="https://servicepro.co/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className="jobsite:underline"
              >
                Privacy
              </a>
              .
            </p>
          </div>
        )}

        {/* ── OTP ── */}
        {step === "otp" && (
          <div className="jobsite:space-y-4">
            <OtpBoxes code={code} setCode={setCode} />
            {err && (
              <p className="jobsite:text-center jobsite:text-sm jobsite:font-medium jobsite:text-destructive">
                {err}
              </p>
            )}
            <div className="jobsite:space-y-3">
              <Button
                type="button"
                size="lg"
                disabled={!editor && (code.length !== 6 || busy)}
                onClick={verify}
                className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
              >
                {busy ? <Loader2 className="jobsite:h-4 jobsite:w-4 jobsite:animate-spin" /> : "Verify"}
              </Button>
              <BackLink onClick={() => setStep("phone")} />
            </div>
          </div>
        )}

        {/* ── PROFILE (new customers) ── */}
        {step === "profile" && (
          <div className="jobsite:space-y-4">
            <div className="jobsite:grid jobsite:grid-cols-2 jobsite:gap-3">
              <Input
                autoFocus
                placeholder="First name"
                value={profile.first}
                onChange={(e) => setProfile({ ...profile, first: e.target.value })}
                className="jobsite:h-12 jobsite:text-base"
              />
              <Input
                placeholder="Last name"
                value={profile.last}
                onChange={(e) => setProfile({ ...profile, last: e.target.value })}
                className="jobsite:h-12 jobsite:text-base"
              />
            </div>
            <Input
              type="email"
              inputMode="email"
              placeholder="Email for your receipt"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              className="jobsite:h-12 jobsite:text-base"
            />
            <label className="jobsite:flex jobsite:cursor-pointer jobsite:items-start jobsite:gap-2.5">
              <input
                type="checkbox"
                checked={termsConsent}
                onChange={(e) => setTermsConsent(e.target.checked)}
                className="jobsite:mt-0.5 jobsite:h-[18px] jobsite:w-[18px] jobsite:shrink-0 jobsite:accent-primary"
              />
              <span className="jobsite:text-[12px] jobsite:leading-relaxed jobsite:text-muted-foreground">
                I agree to the Terms &amp; Cancellation policy.
              </span>
            </label>
            <Button
              type="button"
              size="lg"
              disabled={
                !editor &&
                (!profile.first.trim() ||
                  !profile.last.trim() ||
                  !/\S+@\S+\.\S+/.test(profile.email) ||
                  !termsConsent)
              }
              onClick={() => setStep("review")}
              className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
            >
              Review request
            </Button>
          </div>
        )}

        {/* ── REVIEW ── */}
        {step === "review" && svc && (
          <div className="jobsite:space-y-4">
            <div className="jobsite:rounded-xl jobsite:border jobsite:border-border jobsite:px-4">
              <SummaryRow icon={<Check className="jobsite:h-4 jobsite:w-4" />} label="Service">
                {svc.name}
              </SummaryRow>
              <SummaryRow icon={<Calendar className="jobsite:h-4 jobsite:w-4" />} label="Preferred time">
                {dayLabel} · {windowLabel || (editor ? "Morning" : "")}
              </SummaryRow>
              <SummaryRow icon={<Clock className="jobsite:h-4 jobsite:w-4" />} label="Requested by">
                {name || (editor ? "Alex Rivera" : "—")}
                <span className="jobsite:font-normal jobsite:text-muted-foreground">
                  {" · "}
                  {phone || (editor ? "(512) 555-0148" : "")}
                </span>
              </SummaryRow>
              {note.trim() && (
                <SummaryRow icon={<MessageSquare className="jobsite:h-4 jobsite:w-4" />} label="Note">
                  <span className="jobsite:font-normal">{note.trim()}</span>
                </SummaryRow>
              )}
            </div>
            {err && <p className="jobsite:text-sm jobsite:font-medium jobsite:text-destructive">{err}</p>}
            <div className="jobsite:space-y-3">
              <Button
                type="button"
                size="lg"
                disabled={!editor && busy}
                onClick={confirm}
                className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
              >
                {busy ? <Loader2 className="jobsite:h-4 jobsite:w-4 jobsite:animate-spin" /> : "Send request"}
              </Button>
              <BackLink onClick={() => setStep(known ? "select" : "profile")} />
            </div>
            <p className="jobsite:text-center jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground">
              By sending this request, you agree to ServicePro's{" "}
              <a
                href="https://servicepro.co/terms-of-service"
                target="_blank"
                rel="noreferrer"
                className="jobsite:underline"
              >
                Terms
              </a>{" "}
              &amp;{" "}
              <a
                href="https://servicepro.co/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className="jobsite:underline"
              >
                Privacy
              </a>
              .
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ──────────────────── the standalone section ──────────────────── */

export interface BookingWidgetProps extends BaseComponentProps {
  heading?: string;
  subheading?: string;
  /** Resolved by the Puck config from business context / window config. */
  slug?: string;
  apiUrl?: string;
  businessName?: string;
  businessPhone?: string;
  backgroundColor?: string;
}

export const BookingWidget: React.FC<BookingWidgetProps> = ({
  id,
  heading,
  subheading,
  slug,
  apiUrl,
  businessName,
  businessPhone,
  backgroundColor,
  className,
}) => {
  // Resolve runtime config at render for the live jobsite (props win when the
  // Puck config supplied them from metadata).
  const config = getWindowJobsiteConfig();
  const business = config.business as JobsiteBusinessContext | undefined;
  const resolvedApiUrl = apiUrl ?? config.apiUrl;
  const resolvedSlug = slug ?? business?.slug;

  return (
    <section
      id={id || "book"}
      className={cn("jobsite:py-16 jobsite:md:py-24 jobsite:bg-background", className)}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        <div className="jobsite:mx-auto jobsite:max-w-xl">
          {(heading || subheading) && (
            <div className="jobsite:mb-8 jobsite:text-center">
              {heading && (
                <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:text-foreground jobsite:md:text-5xl">
                  {heading}
                </h2>
              )}
              {subheading && (
                <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-muted-foreground jobsite:md:text-xl">
                  {subheading}
                </p>
              )}
            </div>
          )}
          <BookingWidgetInline
            slug={resolvedSlug}
            apiUrl={resolvedApiUrl}
            businessName={businessName ?? business?.name}
            businessPhone={businessPhone ?? business?.phone}
          />
        </div>
      </div>
    </section>
  );
};
